import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StateStoreService } from '../services/state-store/state-store';

/**
 * Sends a delegate with nothing to work on to `/no-workspace`.
 *
 * Applied to every authenticated route (see `app.routes.ts`, which splices
 * it in immediately after `profileCompletionGuard`), which is what satisfies
 * T7's *"revoking the grant lands them on the empty-workspace screen on
 * their next navigation — no re-login"*: authorisation is never carried in
 * the token, so the next navigation re-evaluates this and the user moves
 * without touching their session.
 *
 * Ordering matters twice over:
 *
 *   - **After `profileCompletionGuard`.** A user who has not finished
 *     onboarding should finish it first; that is a step they can act on,
 *     whereas this screen is a dead end until someone else acts.
 *   - **Before the role guards** (`managerGuard`, `complianceGuard`, …).
 *     Those redirect to `/dashboard` or `/assessment`, which are themselves
 *     guarded — so letting one of them win first would bounce a
 *     workspace-less delegate between two guarded routes instead of landing
 *     them on an explanation.
 *
 * Narrow by construction: `hasNoWorkspace()` is false for every non-delegate
 * and for a delegate who holds platform standing on their own member row, so
 * this guard is a no-op for everyone except the accounts it exists for.
 */
export const workspaceGuard: CanActivateFn = () => {
  const state = inject(StateStoreService);
  const router = inject(Router);

  return state.hasNoWorkspace() ? router.createUrlTree(['/no-workspace']) : true;
};

/**
 * The inverse, guarding `/no-workspace` itself.
 *
 * Without it the screen is a trap: a user who gets access while sitting on
 * it — or who bookmarks the URL, or lands on it in a stale tab — would have
 * no way back into the product. Both guards read the same signal, so they
 * cannot disagree about who belongs where.
 */
export const noWorkspaceOnlyGuard: CanActivateFn = () => {
  const state = inject(StateStoreService);
  const router = inject(Router);

  return state.hasNoWorkspace() ? true : router.createUrlTree(['/dashboard']);
};
